import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    Paper,
    Typography,
    Button,
    Stack,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Card,
    CardContent,
    InputLabel,
} from "@mui/material";
import DatePickerComponent from "../../Common/DatePicker";
import Select from "../../Common/Select";
import MultipleSelectJson from "../../Common/MultipleSelectJson";
import { Clear as ClearIcon } from "@mui/icons-material";

import { useGenerateCustomersReportQuery } from "../../../store/reports/reportsApis";
import { useGetRegionsDropdownQuery } from "../../../store/masters/mastersApis";
import { useGetUsersDropdownQuery } from "../../../store/users/usersApis";

const CustomersReport = ({
    reportType,
    filters,
    onFilterUpdate,
    onFilterReset,
    onExport,
    onRefresh,
    onReportDataUpdate,
}) => {
    const [localFilters, setLocalFilters] = useState(filters);

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    // Fetch dropdown data
    const { data: regionsData } = useGetRegionsDropdownQuery();
    const { data: staffData } = useGetUsersDropdownQuery({ role: "staff" });

    const { data: reportData, isLoading, error, refetch } = useGenerateCustomersReportQuery(localFilters);

    // Update parent with report data whenever it changes
    useEffect(() => {
        if (reportData && onReportDataUpdate) {
            onReportDataUpdate("customers", reportData);
        }
    }, [reportData, onReportDataUpdate]);

    // Convert IDs to objects for MultipleSelectJson display
    const convertIdsToObjects = (ids, options) => {
        if (!Array.isArray(ids) || ids.length === 0) return [];
        return ids.map((id) => options.find((option) => option.value === id)).filter(Boolean);
    };

    const handleFilterChange = (field, value) => {
        // Extract values from MultipleSelectJson objects
        let processedValue = value;
        if (field.includes("_ids") && Array.isArray(value)) {
            processedValue = value.map((item) => item.value || item);
            // Remove duplicates
            processedValue = [...new Set(processedValue)];
        }

        const newFilters = { ...localFilters, [field]: processedValue };
        setLocalFilters(newFilters);
        onFilterUpdate(newFilters);
    };

    const handleReset = () => {
        setLocalFilters({
            start_date: null,
            end_date: null,
            region_ids: [],
            created_by_ids: [],
            format: "json",
        });
        onFilterReset();
    };

    return (
        <Box>
            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Filters
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <DatePickerComponent
                            label="Start Date"
                            value={localFilters.start_date}
                            onChange={(date) => handleFilterChange("start_date", date)}
                            name="start_date"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <DatePickerComponent
                            label="End Date"
                            value={localFilters.end_date}
                            onChange={(date) => handleFilterChange("end_date", date)}
                            name="end_date"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "6px" }}>
                            <InputLabel sx={{ fontSize: "14px", color: "#454545", fontWeight: "500" }} shrink={false}>
                                Regions
                            </InputLabel>
                            <MultipleSelectJson
                                id="region-select"
                                label="Regions"
                                value={convertIdsToObjects(localFilters.region_ids || [], regionsData || [])}
                                onChange={(value) => handleFilterChange("region_ids", value)}
                                name="region_ids"
                                options={regionsData || []}
                                size="small"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "6px" }}>
                            <InputLabel sx={{ fontSize: "14px", color: "#454545", fontWeight: "500" }} shrink={false}>
                                Staff Members
                            </InputLabel>
                            <MultipleSelectJson
                                id="staff-select"
                                label="Staff Members"
                                value={convertIdsToObjects(
                                    localFilters.created_by_ids || [],
                                    staffData?.results?.map((staff) => ({ value: staff.id, label: staff.name })) || [],
                                )}
                                onChange={(value) => handleFilterChange("created_by_ids", value)}
                                name="created_by_ids"
                                options={
                                    staffData?.results?.map((staff) => ({ value: staff.id, label: staff.name })) || []
                                }
                                size="small"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Select
                            label="Format"
                            value={localFilters.format || "json"}
                            onChange={(e) => handleFilterChange("format", e.target.value)}
                            name="format"
                            options={[
                                { value: "json", label: "JSON" },
                                { value: "csv", label: "CSV" },
                                { value: "excel", label: "Excel" },
                            ]}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" startIcon={<ClearIcon />} onClick={handleReset} size="small">
                                Reset
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {isLoading && (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error?.data?.message || "Failed to generate customers report"}
                </Alert>
            )}

            {reportData && !isLoading && (
                <Box>
                    {/* Summary Cards */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Total Customers
                                    </Typography>
                                    <Typography variant="h4">{reportData.summary?.total_customers || 0}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Orders per Customer
                                    </Typography>
                                    <Typography variant="h4">{reportData.summary?.orders_per_customer || 0}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Card>
                                <CardContent>
                                    <Typography color="textSecondary" gutterBottom>
                                        Total Orders Value
                                    </Typography>
                                    <Typography variant="h4">
                                        ₹{reportData.summary?.total_orders_value?.toLocaleString() || 0}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Region Breakdown */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom>
                                    Region Breakdown
                                </Typography>
                                {reportData.summary?.region_breakdown && (
                                    <Box>
                                        {Object.entries(reportData.summary.region_breakdown).map(([region, count]) => (
                                            <Box key={region} display="flex" justifyContent="space-between" mb={1}>
                                                <Typography variant="body2">{region}</Typography>
                                                <Chip label={count} size="small" />
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>

                    {/* Customers Table */}
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            Customers Details ({reportData.customers?.length || 0} customers)
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Mobile</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Region</TableCell>
                                        <TableCell>Staff</TableCell>
                                        <TableCell>Orders Count</TableCell>
                                        <TableCell>Created Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.customers?.map((customer) => (
                                        <TableRow key={customer.id}>
                                            <TableCell>{customer.name}</TableCell>
                                            <TableCell>{customer.mobile}</TableCell>
                                            <TableCell>{customer.email}</TableCell>
                                            <TableCell>{customer.region?.name}</TableCell>
                                            <TableCell>{customer.staff?.name}</TableCell>
                                            <TableCell>{customer.orders?.length || 0}</TableCell>
                                            <TableCell>{new Date(customer.created_at).toLocaleDateString()}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
            )}

            {!reportData && !isLoading && !error && (
                <Alert severity="info">No data found for the selected filters.</Alert>
            )}
        </Box>
    );
};

export default CustomersReport;
